import type { Task } from "@app/core";

/**
 * Keep duration semantics valid when a time-ladder choice changes an item's
 * scheduling mode. All-day durations occupy whole logical days; converting an
 * all-day item back to a clock time uses the configured default block length,
 * matching the calendar's all-day-to-timed drag contract.
 */
export function ladderDurationMinutes(task: Task, allDay: boolean, defaultDurationMin: number): number {
  if (allDay === task.all_day) return task.duration_minutes;
  if (allDay) return Math.max(1440, Math.ceil(Math.max(0, task.duration_minutes) / 1440) * 1440);
  return Math.max(1, Math.round(defaultDurationMin));
}

/** The all-day rung is a conversion for timed work, but the current-state
 * choice for an already all-day item. Same-day callers hide only the latter. */
export function shouldShowAllDayLadderChoice(task: Task, showCurrentChoice: boolean): boolean {
  // An undated item has no current schedule: even when its stored mode is
  // all-day, choosing this rung is the operation that actually puts it on the
  // target date. Only hide the rung for an already-scheduled all-day item when
  // a same-day caller suppresses current-state choices.
  return !task.due_at || !task.all_day || showCurrentChoice;
}

/** True when a ladder result is the task's existing schedule. Compare the
 * instant, not the raw ISO spelling: imported/legacy values may omit
 * milliseconds even though they identify the same time. */
export function sameLadderSchedule(task: Task, due: Date, allDay: boolean): boolean {
  if (!task.due_at || !!task.all_day !== allDay) return false;
  const current = new Date(task.due_at).getTime();
  return Number.isFinite(current) && current === due.getTime();
}
